import type { System } from "./types";
import { migrate, type MigrateError } from "./schema/migrate";

/** Legacy autosave key, retained for migration and return-visitor detection. */
export const STORAGE_KEY = "twelve-volt:system";

/** Synchronous companion to the IndexedDB project store. The landing gate has
 * to decide before first paint, so it cannot await Dexie. */
export const CANVAS_USED_STORAGE_KEY = "twelve-volt:canvas-used-v1";

export function markCanvasUsed(): void {
  try {
    localStorage.setItem(CANVAS_USED_STORAGE_KEY, "1");
  } catch {
    // Storage may be disabled; the canvas itself still works through Dexie.
  }
}

/**
 * Thrown by `readSystemFile` when a file cannot be loaded. Carries the
 * underlying `MigrateError` discriminator so the UI can render a meaningful
 * message instead of a generic alert.
 */
export class SystemFileError extends Error {
  constructor(public readonly cause: MigrateError | { kind: "parse"; message: string }) {
    super(cause.kind === "future-version"
      ? `File schema v${cause.fileVersion} is newer than supported v${cause.supportedVersion}`
      : cause.message ?? "Failed to read system file");
    this.name = "SystemFileError";
  }
}

export function saveToLocalStorage(system: System) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(system));
    localStorage.setItem(CANVAS_USED_STORAGE_KEY, "1");
  } catch (e) {
    console.warn("Failed to save to localStorage", e);
  }
}

/**
 * True when this browser has been in the canvas before. The dedicated marker
 * follows the IndexedDB project store; the legacy payload keeps pre-migration
 * browsers working. Never parses, so it stays cheap enough for pre-paint use.
 */
export function hasStoredSystem(): boolean {
  try {
    return (
      localStorage.getItem(CANVAS_USED_STORAGE_KEY) !== null ||
      localStorage.getItem(STORAGE_KEY) !== null
    );
  } catch {
    return false;
  }
}

export function loadFromLocalStorage(): System | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const result = migrate(parsed);
    if (!result.ok) {
      console.warn("Discarding incompatible localStorage payload", result.error);
      return null;
    }
    return result.system;
  } catch (e) {
    console.warn("Failed to load from localStorage", e);
    return null;
  }
}

export function downloadSystem(system: System, installationId?: string) {
  // Recovery metadata is outside the electrical model. It is only a locator:
  // the server still verifies the signed-in owner and active paid entitlement.
  const file = installationId ? { ...system, _crimp_installation: { id: installationId } } : system;
  const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeName = (system.name || "system").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase();
  a.href = url;
  a.download = `${safeName}.12v.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function readSystemFile(file: File): Promise<System> {
  return (await readSystemFileWithInstallation(file)).system;
}

export function readSystemFileWithInstallation(file: File): Promise<{ system: System; installationId: string | null }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(reader.result as string);
      } catch (e) {
        reject(new SystemFileError({ kind: "parse", message: (e as Error).message }));
        return;
      }
      let installationId: string | null = null;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const fileObject = { ...parsed } as Record<string, unknown>;
        const reference = fileObject._crimp_installation;
        if (reference && typeof reference === "object" && "id" in reference && typeof reference.id === "string" &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(reference.id)) {
          installationId = reference.id;
        }
        delete fileObject._crimp_installation;
        parsed = fileObject;
      }
      const result = migrate(parsed);
      if (!result.ok) {
        reject(new SystemFileError(result.error));
        return;
      }
      resolve({ system: result.system, installationId });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}
